import { useEffect } from "react";
import {
  Autocomplete,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Grid } from "@mui/system";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import CalculationTypeFilterItems from "./CalculationTypeFilterItems";
import {
  setOpenModalType,
  setSelectedCalculationName,
} from "../../../../features/unifierCalculationTypesSlice";
import { selectUserData } from "../../../../features/userSlice";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";

const CalculationTypesTableHead = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { unifierCalculationTypesList, selectedCalculationName } = useSelector(
    (state: any) => state.unifierCalculationTypes,
  );

  const response = useSelector(selectUserData);
  const hasUnifierCreateEditCalculationTypesPermission = CheckPermissions(
    response?.data?.products?.data_collection,
    "unifier",
    "create/edit_calculation_types",
  );

  useEffect(() => {
    dispatch(setSelectedCalculationName(searchParams.get("search")));
    // eslint-disable-next-line
  }, []);

  const clearSelectedCalculationName = () => {
    dispatch(setSelectedCalculationName(null));
  };

  const handleSelectCalculationName = (newValue: string | null) => {
    dispatch(setSelectedCalculationName(newValue));
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      if (newValue) {
        updatedParams.set("search", newValue);
      } else {
        updatedParams.delete("search");
      }
      // a narrower list can be shorter than the page the user is on
      updatedParams.set("page", "0");
      return updatedParams;
    });
  };

  const handleOpenCreateModal = () => {
    dispatch(setOpenModalType("create"));
  };

  // the same name can be used by several types, so the options are deduped
  const calculationNameOptions = [
    ...new Set(
      (unifierCalculationTypesList ?? []).map((item: any) => item?.name),
    ),
  ].filter(Boolean) as string[];

  return (
    <Box sx={{ padding: "10px 0px 0px" }}>
      <Typography variant="body2" sx={{ fontSize: "14px", mb: 2 }}>
        View, create and edit the calculation types used for extracting the
        correct term for unifying the data from raw tables to the destination
        tables.
      </Typography>

      <Grid container rowSpacing={2} columnSpacing={3} alignItems="center">
        <Grid size={3}>
          <Autocomplete
            sx={{ width: "100%" }}
            options={calculationNameOptions}
            value={selectedCalculationName}
            onChange={(_, newValue) => handleSelectCalculationName(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Calculation Name"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                }}
                color="secondary"
              />
            )}
            size="small"
          />
        </Grid>
        <Grid size={6}>
          <CalculationTypeFilterItems
            clearSelectedCalculationName={clearSelectedCalculationName}
          />
        </Grid>
        <Grid size={3}>
          <Box display="flex" justifyContent="flex-end">
            <Button
              data-tracker="375"
              variant="contained"
              color="secondary"
              sx={{ whiteSpace: "nowrap" }}
              disabled={!hasUnifierCreateEditCalculationTypesPermission}
              onClick={handleOpenCreateModal}
            >
              Create new Type <AddIcon sx={{ width: "20px", ml: "5px" }} />
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CalculationTypesTableHead;
